/**
 * MODULE: AssistantVoiceWaveformButton.tsx
 * PURPOSE: Perplexity-style circular voice-mode control with animated waveform bars.
 */

type AssistantVoiceWaveformButtonProps = {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  immersive?: boolean;
};

const BAR_DELAYS = ['0ms', '120ms', '240ms', '120ms', '0ms'] as const;

function WaveformBars({ active }: { active: boolean }) {
  return (
    <span className="inline-flex h-4 items-end justify-center gap-[2px]" aria-hidden>
      {BAR_DELAYS.map((delay, index) => (
        <span
          key={delay}
          className={[
            'w-[2px] rounded-full bg-gray-900 dark:bg-gray-900',
            index === 0 || index === 4 ? 'h-[4px]' : index === 2 ? 'h-[14px]' : 'h-[9px]',
            active ? 'origin-bottom animate-[assistant-voice-bar_0.85s_ease-in-out_infinite]' : '',
          ].join(' ')}
          style={active ? { animationDelay: delay } : undefined}
        />
      ))}
    </span>
  );
}

export function AssistantVoiceWaveformButton({
  active,
  disabled = false,
  onClick,
  immersive = false,
}: AssistantVoiceWaveformButtonProps) {
  return (
    <>
      <style>{`
        @keyframes assistant-voice-bar {
          0%, 100% { transform: scaleY(0.35); }
          50% { transform: scaleY(1); }
        }
      `}</style>
      <button
        type="button"
        data-testid="assistant-voice-mode-button"
        onClick={onClick}
        disabled={disabled}
        aria-label={active ? 'Stop voice mode' : 'Start voice mode'}
        title={active ? 'Stop listening' : 'Voice mode — speak to send'}
        className={[
          'inline-flex h-9 w-9 items-center justify-center rounded-full transition',
          immersive
            ? active
              ? 'bg-white ring-2 ring-white/30'
              : 'bg-white hover:bg-gray-100'
            : active
              ? 'bg-gray-200 ring-2 ring-gray-400/50 dark:bg-gray-300'
              : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-200 dark:hover:bg-gray-300',
          disabled ? 'cursor-not-allowed opacity-40' : '',
        ].join(' ')}
      >
        <WaveformBars active={active} />
      </button>
    </>
  );
}
